import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createRefetchContainer, graphql } from 'react-relay';
import serverVars from 'server-vars';
import { CUSTOMER_TYPE_TRADE } from '../../constants/userConstants';
import { sbRespRefineMenuFiltersMap } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap';
import {
    hasPersonalizedFilterQueryParam,
    getUserZipCode,
} from '../../shared/helpers/personalizedFiltersHelpers';
/* Components */
import { SharedUserAndRelayVarLoader } from '../../shared/SharedUserAndRelayVarLoader';
import { SbSharedRefetchContainer } from '../SbSharedRefetchContainer/SbSharedRefetchContainer';
import { SbRespComponents } from '../SbRespComponents/SbRespComponents';
import { SbSharedHead } from '../SbSharedHead/SbSharedHead';
import { SbSharedTrackingContainer } from '../SbSharedTrackingContainer/SbSharedTrackingContainer';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { SbSharedSearchLandingLayout } from '../SbSharedSearchLandingLayout/SbSharedSearchLandingLayout';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';
const { SEARCH } = pageTypes;

/* Styles */
import './SbRespLayout.scss'; // temp fix for FINDING-13444, will eventually change base style in DBL

class SbRespLayoutComponent extends React.Component {
    constructor() {
        super();

        this.handleSbRefetch = this.handleSbRefetch.bind(this);
        this.handleUserRefetch = this.handleUserRefetch.bind(this);

        this.relayVars = {};
        this.state = {
            hasFirstRefetchFinished: false,
        };
    }

    handleUserRefetch(variables, ...args) {
        /**
         * Need to refetch sb items for trade users, the price depends on the `isTrade` variable
         * being set.
         *
         * Only do this after login, hence the server vars check, subsequent visits will depend on
         * the ut query param.
         */
        const { isClient } = variables || {};
        const hasPersonalizedFilter = isClient && hasPersonalizedFilterQueryParam();
        const userZipCode = getUserZipCode();
        const fetchSb = variables.isTrade && serverVars.get('userType') !== CUSTOMER_TYPE_TRADE;
        const { viewer, relayVariables } = this.props;
        const { displayUriRef } = (viewer && viewer.itemSearch) || {};
        const { followSearchPages } = relayVariables || {};
        this.doRefetch(
            {
                ...variables,
                fetchSb,
                skipUser: false,
                hasPersonalizedFilter,
                userZipCode,
                isClient: true,
                followSearchPages: displayUriRef ? [displayUriRef] : followSearchPages,
            },
            ...args
        );
    }

    handleSbRefetch(variables, renderVariables, callback) {
        this.doRefetch({ ...variables, fetchSb: true }, renderVariables, () => {
            callback();

            if (!this.state.hasFirstRefetchFinished) {
                this.setState({ hasFirstRefetchFinished: true });
            }

            this.props.runNotificationQueue();
        });
    }

    doRefetch(variables, renderVariables, callback) {
        this.relayVars = { ...this.relayVars, ...variables };
        this.props.relay.refetch(
            this.relayVars,
            { ...this.relayVars, ...renderVariables, fetchSb: true },
            callback
        );
    }

    render() {
        const { viewer, localeDomain, reinitTracking, isHashFragmentResolved } = this.props;
        const user = viewer.user || null;
        const {
            itemSearch,
            regionalInfo = null,
            shippingFilters = null,
            enableSearchLandingPage,
        } = viewer;

        const isSearch = itemSearch.pageType === SEARCH;
        const isHashFragmentSearchPage =
            isSearch && !this.context.isSearchQueryApplied && enableSearchLandingPage;
        const { hasFirstRefetchFinished } = this.state;

        return (
            <React.Fragment>
                <SharedUserAndRelayVarLoader onUserRefetch={this.handleUserRefetch} />
                <CurrencyProvider
                    render={({ currency }) => (
                        <React.Fragment>
                            <SbSharedTrackingContainer
                                user={user}
                                currency={currency}
                                itemSearch={itemSearch}
                                reinitTracking={reinitTracking}
                                refineMenuFiltersMap={sbRespRefineMenuFiltersMap}
                            />
                            <SbSharedHead itemSearch={itemSearch} localeDomain={localeDomain} />
                            <SbSharedRefetchContainer
                                viewer={viewer}
                                itemSearch={itemSearch}
                                regionalInfo={regionalInfo}
                                shippingFilters={shippingFilters}
                                onSbRefetch={this.handleSbRefetch}
                            />
                            {isHashFragmentSearchPage && !hasFirstRefetchFinished ? (
                                <SbSharedSearchLandingLayout isVisible={isHashFragmentResolved} />
                            ) : (
                                <SbRespComponents
                                    viewer={viewer}
                                    itemSearch={itemSearch}
                                    user={user}
                                    currency={currency}
                                    isClient={!!this.relayVars.isClient}
                                    shippingFilters={shippingFilters}
                                />
                            )}
                        </React.Fragment>
                    )}
                />
            </React.Fragment>
        );
    }
}

SbRespLayoutComponent.propTypes = {
    viewer: PropTypes.object,
    relay: PropTypes.object.isRequired,
    localeDomain: PropTypes.string.isRequired,
    runNotificationQueue: PropTypes.func.isRequired,
    reinitTracking: PropTypes.func,
    isHashFragmentResolved: PropTypes.bool.isRequired,
    relayVariables: PropTypes.shape({
        followSearchPages: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
};

SbRespLayoutComponent.defaultProps = {
    runNotificationQueue: () => {},
};

SbRespLayoutComponent.contextType = ServerVarsContext;

const mapStateToProps = state => {
    const { intlVariables, filters, relayVariables } = state;

    return {
        localeDomain: intlVariables.localeDomain,
        isHashFragmentResolved: filters.isHashFragmentResolved,
        relayVariables: relayVariables.variables,
    };
};

export const SbRespLayout = createRefetchContainer(
    connect(mapStateToProps)(SbRespLayoutComponent),
    {
        viewer: graphql`
            fragment SbRespLayout_viewer on Viewer
                @argumentDefinitions(
                    hasPersonalizedFilter: { type: "Boolean", defaultValue: false }
                    userZipCode: { type: "String", defaultValue: "0" }
                ) {
                enableSearchLandingPage: isEnabled(key: "enableSearchLandingPage")
                ...SbSharedRefetchContainer_viewer
                ...SbRespComponents_viewer
                itemSearch(
                    uriRef: $uriRef
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    eligibleAbTest: $eligibleAbTest
                    disableForceFacet: $disableForceFacet
                ) {
                    pageType
                    displayUriRef
                    ...SbSharedRefetchContainer_itemSearch
                    ...SbSharedTrackingContainer_itemSearch
                    ...SbSharedHead_itemSearch
                    ...SbRespComponents_itemSearch @include(if: $fetchSb)
                }
                user(userId: $userId) @include(if: $fetchUser) {
                    ...SbSharedTrackingContainer_user
                    ...SbRespComponents_user
                }
                regionalInfo(zipCode: $userZipCode) @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_regionalInfo
                }
                shippingFilters(uriRef: $uriRef, zipCode: $userZipCode)
                    @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_shippingFilters
                    ...SbRespComponents_shippingFilters
                }
            }
        `,
    },
    graphql`
        query SbRespLayoutRefetchQuery(
            $userId: String = ""
            $fetchUser: Boolean!
            $fetchSb: Boolean!
            $skipUser: Boolean = true
            $isTrade: Boolean!
            $uriRef: String
            $first: Int!
            $page: Int!
            $localeOrigin: String = ""
            $showSeller: Boolean!
            $followSearchPages: [String]
            $followSearchTypes: [types] = [SEARCH]
            $contentModuleId: String = ""
            $eligibleAbTest: String = ""
            $hasPersonalizedFilter: Boolean!
            $userZipCode: String = "0"
            $isClient: Boolean!
            $disableForceFacet: Boolean = true
        ) {
            viewer {
                enableSearchLandingPage: isEnabled(key: "enableSearchLandingPage")
                ...SbSharedRefetchContainer_viewer
                ...SbRespComponents_viewer @skip(if: $fetchSb)
                itemSearch(
                    uriRef: $uriRef
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    eligibleAbTest: $eligibleAbTest
                    disableForceFacet: $disableForceFacet
                ) @include(if: $fetchSb) {
                    pageType
                    displayUriRef
                    ...SbSharedRefetchContainer_itemSearch
                    ...SbRespComponents_itemSearch
                    ...SbSharedHead_itemSearch
                    ...SbSharedTrackingContainer_itemSearch
                }
                user(userId: $userId) @include(if: $fetchUser) @skip(if: $skipUser) {
                    ...SbSharedTrackingContainer_user
                    ...SbRespComponents_user
                }
                regionalInfo(zipCode: $userZipCode) @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_regionalInfo
                }
                shippingFilters(uriRef: $uriRef, zipCode: $userZipCode)
                    @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_shippingFilters
                    ...SbRespComponents_shippingFilters
                }
            }
        }
    `
);
